import { VehicleRowCardVM } from 'src/pages/ConvenienceDispatches/vehicleRowVM';

/**
 * 停車地を並べ替えたときの、行のカード列。
 *
 * VehicleRowVM のカードを並べ替えるのは、左リストのカード列と地図のルート線・
 * 番号マーカーが同じ VM から作られるため。ここを差し替えると両方が同時に動く。
 *
 * operation を持たないカード（再積み込み拠点・固定ルートの仮カード）は動かす
 * 手がかりが無いので、元の位置に残す。
 */
export function reorderRowCardsByOperationIds(
  cards: VehicleRowCardVM[],
  orderedOperationIds: readonly number[],
): VehicleRowCardVM[] {
  if (orderedOperationIds.length === 0) return cards;

  const rankByOperationId = new Map<number, number>();
  orderedOperationIds.forEach((id, index) => {
    if (!rankByOperationId.has(id)) rankByOperationId.set(id, index);
  });

  const rankOf = (card: VehicleRowCardVM): number | null => {
    const ranks = card.operations
      .map((op) => rankByOperationId.get(op.id))
      .filter((rank): rank is number => rank != null);
    return ranks.length === 0 ? null : Math.min(...ranks);
  };

  // 動かせるカードだけを抜き出して並べ替え、動かせないカードは元の添字に戻す
  const fixedByIndex = new Map<number, VehicleRowCardVM>();
  const movable: { card: VehicleRowCardVM; rank: number }[] = [];
  cards.forEach((card, index) => {
    const rank = rankOf(card);
    if (rank == null) fixedByIndex.set(index, card);
    else movable.push({ card, rank });
  });
  if (movable.length === 0) return cards;

  // Array#sort は安定なので、同じ rank のカードは元の順のまま
  const sorted = [...movable].sort((a, b) => a.rank - b.rank);

  const next: VehicleRowCardVM[] = [];
  let movableIndex = 0;
  for (let index = 0; index < cards.length; index += 1) {
    const fixed = fixedByIndex.get(index);
    next.push(fixed ?? sorted[movableIndex++].card);
  }

  // 参照が変わると React が全カードを作り直す。並びが同じなら元の配列を返す
  return next.every((card, index) => card === cards[index]) ? cards : next;
}
